export default function AboutSection() {
  return (
    <section id="about" className="bg-[#F5F3EF] py-24 md:py-36">
      <div className="w-full px-8 md:px-16">

        {/* Top Label Row */}
        <div className="flex items-center gap-4 mb-12">
          <span className="bg-[#E63946] text-white font-mono text-[10px] uppercase tracking-[3px] px-3 py-1.5 rounded-full whitespace-nowrap">
            About the Project
          </span>
          <div className="flex-1 h-[1px] bg-[#0D0D0D]/15" />
          <span className="font-mono text-[10px] text-[#0D0D0D]/30 uppercase tracking-[2px] whitespace-nowrap">
            01 / 06
          </span>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Left: Giant Number */}
          <div className="hidden md:block md:col-span-1">
            <span
              className="font-display text-[#1D3557]/10 leading-none select-none"
              style={{ fontSize: "120px", writingMode: "vertical-rl" }}
            >
              01
            </span>
          </div>

          {/* Center: Main Text */}
          <div className="md:col-span-7">
            <h2
              className="font-display text-[#0D0D0D] leading-tight mb-10"
              style={{ fontSize: "clamp(44px, 6vw, 80px)" }}
            >
              Redefining Waste<br />
              <span className="text-[#1D3557]">as Wonder</span>
            </h2>

            <div className="space-y-5">
              <p className="font-body text-[#2B2B2B] text-lg leading-relaxed">
                <strong>OFF-PATTERN: Beyond the Standard</strong> is a conceptual design project that challenges our relationship with imperfection. Born from a critical inquiry into the fashion industry&apos;s obsession with flawlessness, this project asks: what if the mistakes, the misfits, and the miscasts were the most interesting things of all?
              </p>
              <p className="font-body text-[#2B2B2B] text-lg leading-relaxed">
                Working in collaboration with <strong>KAPDAA</strong>, a platform dedicated to sustainable textile innovation, we gathered discarded garment materials — the remnants, the off-cuts, the stained and the torn — and gave them a second life as ugly-cute character-based <strong>bag charms</strong>.
              </p>
              <p className="font-body text-[#2B2B2B] text-lg leading-relaxed">
                Each charm is a small, wearable manifesto. A statement that imperfection is not a failure of quality — it&apos;s a marker of humanity, of process, of story. Textile waste becomes a medium. Human error becomes an aesthetic. The standard is questioned, and exceeded.
              </p>
            </div>

            {/* Key Stats Row */}
            <div className="grid grid-cols-3 gap-6 mt-14 pt-10 border-t border-[#0D0D0D]/10">
              {[
                { num: "336,000", label: "Tonnes of textile waste yearly in UK", accent: "text-[#E63946]" },
                { num: "100%", label: "Crafted from discarded materials", accent: "text-[#1D3557]" },
                { num: "∞", label: "Stories in every imperfection", accent: "text-[#E63946]" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className={`font-display text-5xl md:text-6xl ${stat.accent} leading-none mb-2`}>
                    {stat.num}
                  </div>
                  <p className="font-body text-[#2B2B2B]/70 text-xs uppercase tracking-[2px] leading-relaxed">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Stat Display */}
          <div className="md:col-span-4">
            <div className="relative">
              {/* Blue side annotation */}
              <div className="absolute -left-6 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-center gap-3 z-10">
                <div className="w-[2px] h-24 bg-[#1D3557]" />
                <span
                  className="font-mono text-[10px] uppercase tracking-[3px] text-[#1D3557] whitespace-nowrap"
                  style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                >
                  × KAPDAA
                </span>
                <div className="w-[2px] h-24 bg-[#1D3557]" />
              </div>

              <div
                className="rounded-3xl overflow-hidden"
                style={{ aspectRatio: "3/4" }}
              >
                <img
                  src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/d1fec9bc54c306c05bb517e8e52a6ac3.png"
                  alt="Textile waste dump"
                  className="w-full h-full object-cover object-center"
                />
              </div>

              {/* Red offset border */}
              <div className="absolute -bottom-3 -right-3 w-full h-full rounded-3xl border-2 border-[#E63946] -z-10" />
            </div>

            {/* KAPDAA tag */}
            <div className="mt-8 bg-[#1D3557] rounded-2xl px-6 py-4">
              <p className="font-mono text-[10px] uppercase tracking-[3px] text-white/50 mb-1">
                Collaboration Partner
              </p>
              <p className="font-body text-white font-semibold text-lg">KAPDAA</p>
              <p className="font-body text-white/60 text-sm mt-1 leading-relaxed">
                Sustainable Textile Innovation Platform
              </p>
            </div>
          </div>
        </div>

        {/* Full-width Image Strip */}
        <div className="mt-20 rounded-2xl overflow-hidden w-full" style={{ height: "260px" }}>
          <img
            src="https://public.readdy.ai/ai/img_res/2a4bcced51b4fd374070e40f52445b63.jpg"
            alt="Textile waste materials"
            className="w-full h-full object-cover object-top"
          />
        </div>

      </div>
    </section>
  );
}
